import React, { useState, useEffect } from 'react';
import {
    Box, Text, Stack, Group, ActionIcon, ScrollArea, Tooltip, Loader, Divider, NavLink
} from '@mantine/core';
import { Plus, MessageSquare, Trash2, Edit3, Users, ServerCog, Folder, FolderTree, UserCog, ChevronLeft, ChevronRight } from 'lucide-react';
import { useSession } from '../../contexts/SessionContext';
import { useNavigate, useLocation } from 'react-router-dom';
import './sidebar.css';
import { useAuth } from '../../customAuth/authStateHook';
import { LogOut } from "lucide-react";
import { Button } from '@mantine/core';

export const SIDEBAR_WIDTH = 280;
export const SIDEBAR_COLLAPSED_WIDTH = 56;
export const NAVBAR_HEIGHT = 0;

// Create a context or use localStorage to share sidebar state
const getSidebarState = () => {
    if (typeof window !== 'undefined') {
        const stored = localStorage.getItem('sidebarCollapsed');
        return stored === 'true';
    }
    return false;
};

const setSidebarState = (collapsed: boolean) => {
    if (typeof window !== 'undefined') {
        localStorage.setItem('sidebarCollapsed', collapsed.toString());
        // Dispatch custom event for other components to listen
        window.dispatchEvent(new CustomEvent('sidebarToggle', { detail: { collapsed } }));
    }
};

export default function SessionSidebar({ userEmail }: { userEmail?: string | null }) {
    const {
        sessions,
        currentSessionId,
        isLoading,
        setCurrentSessionId,
        setCurrentSessionCollectionId,
        createNewSession,
        updateSession,
        deleteSession
    } = useSession();

    const [editingId, setEditingId] = useState<string | null>(null);
    const [editTitle, setEditTitle] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
    const [sessionStatuses, setSessionStatuses] = useState<{ [key: string]: boolean }>({}); // Track which sessions have messages
    const [isCollapsed, setIsCollapsed] = useState(getSidebarState()); // Sidebar collapsed state - default is open
    const navigate = useNavigate();
    const location = useLocation();
    const { logout } = useAuth();
    const { isSuperAdmin, canAccessInternalTools } = useAuth();

    // Toggle sidebar and persist state
    const toggleSidebar = () => {
        const newState = !isCollapsed;
        setIsCollapsed(newState);
        setSidebarState(newState);
    };

    // Handle navigation after session ID is set
    useEffect(() => {
        if (pendingNavigation && currentSessionId === pendingNavigation) {
            if (location.pathname !== '/assistant/chat') {
                navigate('/assistant/chat');
            }
            setPendingNavigation(null);
        }
    }, [currentSessionId, pendingNavigation, location.pathname, navigate]);

    const handleSessionClick = (sessionId: string, collectionId?: number | null) => {
        setCurrentSessionId(sessionId);
        setCurrentSessionCollectionId(collectionId || null);

        if (location.pathname !== '/assistant/chat') {
            setPendingNavigation(sessionId);
        }
    };

    const handleCreateNewSession = async () => {
        setIsCreating(true);
        const newSessionId = await createNewSession();
        setIsCreating(false);

        // Navigate to chat page after creating new session
        if (newSessionId && location.pathname !== '/assistant/chat') {
            setPendingNavigation(newSessionId);
        }
    };

    const handleDeleteSession = async (sessionId: string) => {
        await deleteSession(sessionId);
    };

    const startEditing = (sessionId: string, currentTitle: string) => {
        setEditingId(sessionId);
        setEditTitle(currentTitle);
    };

    const saveEdit = async (sessionId: string) => {
        if (editTitle.trim()) {
            await updateSession(sessionId, editTitle.trim());
        }
        setEditingId(null);
        setEditTitle('');
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditTitle('');
    };

    const formatDate = (date: Date) => {
        const now = new Date();
        const diff = now.getTime() - date.getTime();
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        if (days === 0) return 'Today';
        if (days === 1) return 'Yesterday';
        if (days < 7) return `${days} days ago`;
        return date.toLocaleDateString();
    };

    // Only show active session highlighting when on chat page
    const isSessionActive = (sessionId: string) => {
        return location.pathname === '/assistant/chat' && currentSessionId === sessionId;
    };

    useEffect(() => {
        const updateSessionStatuses = () => {
            const statuses: { [key: string]: boolean } = {};
            for (const session of sessions) {
                if (session.DateLastTimeQueriedUtc && session.DateCreatedUtc) {
                    const created = new Date(session.DateCreatedUtc).getTime();
                    const lastQueried = new Date(session.DateLastTimeQueriedUtc).getTime();
                    statuses[session.SessionId] = lastQueried > created;
                } else {
                    statuses[session.SessionId] = false;
                }
            }
            setSessionStatuses(statuses);
        };

        if (sessions.length > 0) {
            updateSessionStatuses();
        }
    }, [sessions]);

    const getSessionStatusIndicator = (sessionId: string, index: number) => {
        const hasMessages = sessionStatuses[sessionId];
        const isFirstWithMessages = hasMessages && index === sessions.findIndex(s => sessionStatuses[s.SessionId]);

        if (!hasMessages) {
            // Empty session - show a hollow circle
            return (
                <Tooltip label="Empty session">
                    <div
                        style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            border: '1.5px solid #ccc',
                            backgroundColor: 'transparent',
                            minWidth: 8,
                            flexShrink: 0
                        }}
                    />
                </Tooltip>
            );
        } else if (isFirstWithMessages) {
            // Most recently used session with messages - show a filled green circle
            return (
                <Tooltip label="Most recently used">
                    <div
                        style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: '#8a6a4a',
                            minWidth: 8,
                            flexShrink: 0
                        }}
                    />
                </Tooltip>
            );
        } else {
            // Regular session with messages - show a filled gray circle
            return (
                <Tooltip label="Has messages">
                    <div
                        style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: '#888',
                            minWidth: 8,
                            flexShrink: 0
                        }}
                    />
                </Tooltip>
            );
        }
    };


    return (
        <Box
            style={{
                width: isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
                height: '100vh',
                backgroundColor: '#f7f4ed',
                borderRight: '1px solid #d8d0c4',
                display: 'flex',
                flexDirection: 'column',
                position: 'fixed',
                left: 0,
                top: 0,
                zIndex: 60,
                transition: 'width 0.3s ease',
            }}
        >
            {/* Toggle Button */}
            <Box
                style={{
                    position: 'absolute',
                    right: -16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 70,
                }}
            >
                <Tooltip label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"} position="right">
                    <ActionIcon
                        variant="filled"
                        size="md"
                        radius="xl"
                        onClick={toggleSidebar}
                        style={{
                            boxShadow: '0 4px 12px rgba(44, 36, 22, 0.2)',
                            background: '#2c2416',
                            transition: 'all 0.2s ease',
                            color: '#faf8f3',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05)';
                            e.currentTarget.style.boxShadow = '0 6px 16px rgba(44, 36, 22, 0.3)';
                            e.currentTarget.style.background = '#4a4335';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(44, 36, 22, 0.2)';
                            e.currentTarget.style.background = '#2c2416';
                        }}
                    >
                        {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
                    </ActionIcon>
                </Tooltip>
            </Box>
            {isCollapsed ? (
                <ScrollArea style={{ flex: 1, overflow: 'visible' }} p={6}>
                    <Stack gap="xs" align="center">
                        <Tooltip label="Code Industry" position="right">
                            <Box
                                className="sidebar-brand-collapsed"
                                onClick={() => navigate('/assistant/chat')}
                            >
                                <img
                                    src="/ci-icon.ico"
                                    alt="Code Industry Logo"
                                    className="sidebar-brand-collapsed-image"
                                />
                            </Box>
                        </Tooltip>
                        {/* <Tooltip label="Collections" position="right">
                            <ActionIcon variant={location.pathname === '/collections' ? 'filled' : 'subtle'} color="dark" size="lg" onClick={() => navigate('/collections')}>
                                <Folder size={20} />
                            </ActionIcon>
                        </Tooltip> */}
                        <Tooltip label="Folder Explorer" position="right">
                            <ActionIcon variant={location.pathname === '/folder-explorer' ? 'filled' : 'subtle'} color="dark" size="lg" onClick={() => navigate('/folder-explorer')}>
                                <FolderTree size={20} />
                            </ActionIcon>
                        </Tooltip>
                        {isSuperAdmin && (
                            <Tooltip label="Users" position="right">
                                <ActionIcon variant={location.pathname === '/users' ? 'filled' : 'subtle'} color="dark" size="lg" onClick={() => navigate('/users')}>
                                    <Users size={20} />
                                </ActionIcon>
                            </Tooltip>
                        )}
                        {/* Logs is a Dev tool — gated on internal-tools access, matching the Dev nav group. */}
                        {isSuperAdmin && canAccessInternalTools && (
                            <Tooltip label="Logs" position="right">
                                <ActionIcon variant={location.pathname === '/logs' ? 'filled' : 'subtle'} color="dark" size="lg" onClick={() => navigate('/logs')}>
                                    <ServerCog size={20} />
                                </ActionIcon>
                            </Tooltip>
                        )}
                        <Divider my="xs" w="80%" />
                        <Tooltip label="New conversation" position="right">
                            <ActionIcon variant="light" color="dark" size="lg" onClick={handleCreateNewSession} disabled={isCreating || isLoading}>
                                {isCreating ? <Loader size={20} /> : <Plus size={20} />}
                            </ActionIcon>
                        </Tooltip>
                        {sessions.slice(0, 5).map((session) => (
                            <Tooltip key={session.SessionId} label={session.Name} position="right">
                                <ActionIcon variant={isSessionActive(session.SessionId) ? 'filled' : 'subtle'} color="dark" size="lg" onClick={() => handleSessionClick(session.SessionId, session.CollectionId)}>
                                    <MessageSquare size={20} />
                                </ActionIcon>
                            </Tooltip>
                        ))}
                    </Stack>
                </ScrollArea>
            ) : (
                <>
                    <Box className="sidebar-fixed-nav">
                        <Box
                            mb="md"
                            style={{
                                background: '#1c1c1a',
                                borderRadius: '10px',
                                padding: '10px 14px',
                                margin: '4px 4px 12px',
                            }}
                        >
                            <img
                                src="/logo.svg"
                                alt="Mind Vault Logo"
                                height={30}
                                style={{ objectFit: 'contain', width: '100%', display: 'block' }}
                            />
                        </Box>

                        {isSuperAdmin && (
                            <Box className="sidebar-nav-group">
                                <NavLink
                                    label="Admin"
                                    leftSection={<UserCog size={16} />}
                                    childrenOffset={12}
                                    className={`navlink ${location.pathname === '/users' ? 'activeNavlink' : 'navlink'}`}
                                >
                                    <NavLink
                                        label="Users"
                                        leftSection={<Users size={16} />}
                                        onClick={() => navigate('/users')}
                                        className={`navlink ${location.pathname === '/users' ? 'activeNavlink' : 'navlink'}`} />
                                </NavLink>
                            </Box>
                        )}

                        {/* <NavLink
                            label="Collections"
                            leftSection={<Folder size={16} />}
                            onClick={() => navigate('/collections')}
                            className={`navlink ${location.pathname === '/collections' ? 'activeNavlink' : 'navlink'}`}
                        /> */}

                        <NavLink
                            label="Folder Explorer"
                            leftSection={<FolderTree size={16} />}
                            onClick={() => navigate('/folder-explorer')}
                            className={`navlink ${location.pathname === '/folder-explorer' ? 'activeNavlink' : 'navlink'}`}
                        />

                        {isSuperAdmin && canAccessInternalTools && (
                            <NavLink
                                label="Dev"
                                leftSection={<ServerCog size={16} />}
                                childrenOffset={12}
                                className={`navlink ${location.pathname === '/dev' ? 'activeNavlink' : 'navlink'}`}
                            >
                                <NavLink
                                    label="Logs"
                                    leftSection={<ServerCog size={16} />}
                                    onClick={() => navigate('/logs')}
                                    className={`navlink ${location.pathname === '/logs' ? 'activeNavlink' : 'navlink'}`} />
                                <NavLink
                                    label="Background Jobs"
                                    leftSection={<ServerCog size={16} />}
                                    onClick={() => navigate('/background-jobs')}
                                    className={`navlink ${location.pathname === '/background-jobs' ? 'activeNavlink' : 'navlink'}`} />
                                <NavLink
                                    label="Langfuse"
                                    leftSection={<ServerCog size={16} />}
                                    onClick={() => window.open('https://cloud.langfuse.com/auth/sign-in', '_blank')}
                                    className={`navlink ${location.pathname === '/langfuse' ? 'activeNavlink' : 'navlink'}`} />
                            </NavLink>
                        )}

                        <Divider my="md" />

                        <Box mb="xs" mr="4px" ml="4px">
                            <Group justify="space-between" align="center">
                                <Text fw={600} size="sm">Conversations</Text>
                                <Tooltip label="New conversation">
                                    <ActionIcon
                                        variant="light"
                                        color="dark"
                                        size="sm"
                                        onClick={handleCreateNewSession}
                                        disabled={isCreating || isLoading}
                                        style={{ marginRight: 5 }}
                                    >
                                        {isCreating ? <Loader size={16} /> : <Plus size={16} />}
                                    </ActionIcon>
                                </Tooltip>
                            </Group>
                        </Box>
                    </Box>

                    <ScrollArea className="sidebar-conversations-scroll" scrollbarSize={6}>
                        {isLoading && sessions.length === 0 ? (
                            <Box p="md" style={{ textAlign: 'center' }}>
                                <Loader size="md" />
                                <Text size="sm" c="dimmed" mt="xs">Loading sessions...</Text>
                            </Box>
                        ) : (
                            <Stack gap="xs">
                                {sessions.map((session, index) => (
                                    <Box
                                        key={session.SessionId}
                                        className="sidebar-session-item"
                                        p="sm"
                                        style={{
                                            borderRadius: '8px',
                                            backgroundColor: isSessionActive(session.SessionId) ? '#eee9df' : undefined,
                                            border: isSessionActive(session.SessionId) ? '1px solid #b8aa98' : '1px solid transparent',
                                            boxShadow: isSessionActive(session.SessionId) ? '0 10px 24px rgba(57, 46, 34, 0.08)' : 'none',
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => handleSessionClick(session.SessionId, session.CollectionId)}
                                    >
                                        <Group justify="space-between" align="flex-start" gap="xs">
                                            <Box style={{ flex: 1, minWidth: 0 }}>
                                                <Group gap="xs" mb={4}>
                                                    {getSessionStatusIndicator(session.SessionId, index)}
                                                    <MessageSquare size={14} color={isSessionActive(session.SessionId) ? '#6f5a43' : '#6f675d'} />
                                                    {editingId === session.SessionId ? (
                                                        <input
                                                            type="text"
                                                            value={editTitle}
                                                            onChange={(e) => setEditTitle(e.target.value)}
                                                            onBlur={() => saveEdit(session.SessionId)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') saveEdit(session.SessionId);
                                                                if (e.key === 'Escape') cancelEdit();
                                                            }}
                                                            onClick={(e) => e.stopPropagation()}
                                                            style={{
                                                                border: '1px solid #8a6a4a',
                                                                borderRadius: '4px',
                                                                padding: '2px 6px',
                                                                fontSize: '12px',
                                                                width: '100%',
                                                                background: 'white'
                                                            }}
                                                            autoFocus
                                                        />
                                                    ) : (
                                                        <Text
                                                            size="xs"
                                                            fw={isSessionActive(session.SessionId) ? 600 : 400}
                                                            c={isSessionActive(session.SessionId) ? '#2f271f' : '#4a4335'}
                                                            style={{ wordBreak: 'break-word', flex: 1 }}
                                                            title={session.Name}
                                                        >
                                                            {session.Name.length > 25 ? session.Name.substring(0, 22) + "..." : session.Name}
                                                        </Text>
                                                    )}
                                                </Group>
                                                <Text size="xs" c="dimmed">
                                                    {formatDate(session.DateLastTimeQueriedUtc)}
                                                </Text>
                                            </Box>

                                            <Group gap={4}>
                                                <Tooltip label="Edit title">
                                                    <ActionIcon
                                                        size="xs"
                                                        variant="subtle"
                                                        color="gray"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            startEditing(session.SessionId, session.Name);
                                                        }}
                                                    >
                                                        <Edit3 size={12} />
                                                    </ActionIcon>
                                                </Tooltip>
                                                {sessions.length > 1 && (
                                                    <Tooltip label="Delete conversation">
                                                        <ActionIcon
                                                            size="xs"
                                                            variant="subtle"
                                                            color="red"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeleteSession(session.SessionId);
                                                            }}
                                                        >
                                                            <Trash2 size={12} />
                                                        </ActionIcon>
                                                    </Tooltip>
                                                )}
                                            </Group>
                                        </Group>
                                    </Box>
                                ))}
                                {sessions.length === 0 && !isLoading && (
                                    <Box p="md" style={{ textAlign: 'center' }}>
                                        <Text size="sm" c="dimmed">No conversations yet</Text>
                                        <Text size="xs" c="dimmed" mt="xs">Create your first conversation!</Text>
                                    </Box>
                                )}
                            </Stack>
                        )}
                    </ScrollArea>
                </>
            )}

            <Box p="md" className="sidebar-account-footer">
                {isCollapsed ? (
                    <Tooltip label="Logout" position="right">
                        <ActionIcon
                            variant="subtle"
                            color="gray"
                            size="lg"
                            onClick={logout}
                            style={{ margin: '0 auto', display: 'block' }}
                        >
                            <LogOut size={20} />
                        </ActionIcon>
                    </Tooltip>
                ) : (
                    <Stack gap={10}>
                        {userEmail && (
                            <Text
                                title={userEmail}
                                className="sidebar-user-email"
                            >
                                {userEmail}
                            </Text>
                        )}
                        <Button
                            variant="filled"
                            color="dark"
                            leftSection={<LogOut size={16} />}
                            onClick={logout}
                            className="sidebar-logout-button"
                            fullWidth
                        >
                            Logout
                        </Button>
                    </Stack>
                )}
            </Box>
        </Box>
    );
}
